import { SITE_URL } from "@/lib/constants/site-url"
import {
  BAKERY_ADDRESS,
  BAKERY_EMAIL,
  BAKERY_GEO,
  BAKERY_HOURS,
  BAKERY_PHONE,
  GOOGLE_BUSINESS_PROFILE_URL,
  INSTAGRAM_URL,
  SITE_NAME,
  TIKTOK_URL,
  TOAST_ORDER_URL,
} from "@/lib/constants/site"

// One Bakery entity for every page that describes the business (/, /visit,
// /contact). Each page used to build its own object with no @id, so Google read
// three separate businesses with different service areas and profile links.
// Emit this same node everywhere; the shared @id lets Google merge it.
export const BAKERY_SCHEMA_ID = `${SITE_URL}/#bakery`

const BUSINESS_DESCRIPTION =
  "Asian-fusion bakery in Rancho Cucamonga: salt bread, pandan lattes, matcha, and savory rice bowls. Order online for pickup."

const SERVICE_AREA = [
  ...["Rancho Cucamonga", "Alta Loma", "Etiwanda", "Upland", "Ontario", "Fontana"].map(
    (name) => ({
      "@type": "City",
      name,
      containedInPlace: { "@type": "State", name: "California" },
    }),
  ),
  { "@type": "AdministrativeArea", name: "Inland Empire" },
]

export function buildBakerySchema() {
  return {
    "@context": "https://schema.org",
    // Dual-typed: Google asks for the most specific LocalBusiness subtype, and
    // +Bake is genuinely both — a bakery with a full espresso program.
    "@type": ["Bakery", "CafeOrCoffeeShop"],
    "@id": BAKERY_SCHEMA_ID,
    name: SITE_NAME,
    alternateName: "Plus Bake Cafe",
    url: SITE_URL,
    logo: `${SITE_URL}/images/plusbake/optimized/logo-160.webp`,
    image: `${SITE_URL}/images/plusbake/optimized/og-home-1200x630.jpg`,
    description: BUSINESS_DESCRIPTION,
    telephone: BAKERY_PHONE,
    email: BAKERY_EMAIL,
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: BAKERY_ADDRESS.street,
      addressLocality: BAKERY_ADDRESS.city,
      addressRegion: BAKERY_ADDRESS.region,
      postalCode: BAKERY_ADDRESS.postalCode,
      addressCountry: BAKERY_ADDRESS.country,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: BAKERY_GEO.latitude,
      longitude: BAKERY_GEO.longitude,
    },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ],
      opens: BAKERY_HOURS.opens,
      closes: BAKERY_HOURS.closes,
    },
    servesCuisine: ["Filipino", "Asian Fusion", "Bakery", "Coffee"],
    acceptsReservations: false,
    // Only amenities the café actually has. No aggregateRating/review here:
    // Google ignores self-serving review markup on a LocalBusiness and it can
    // draw a manual action.
    amenityFeature: [
      { "@type": "LocationFeatureSpecification", name: "Free Wi-Fi", value: true },
      { "@type": "LocationFeatureSpecification", name: "Outdoor seating", value: true },
      { "@type": "LocationFeatureSpecification", name: "Free parking", value: true },
    ],
    containedInPlace: { "@type": "Place", name: "Sunrize Center" },
    hasMap: GOOGLE_BUSINESS_PROFILE_URL,
    hasMenu: `${SITE_URL}/menu`,
    areaServed: SERVICE_AREA,
    potentialAction: {
      "@type": "OrderAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: TOAST_ORDER_URL,
        actionPlatform: [
          "https://schema.org/DesktopWebPlatform",
          "https://schema.org/MobileWebPlatform",
        ],
      },
    },
    sameAs: [
      INSTAGRAM_URL,
      TIKTOK_URL,
      "https://www.yelp.com/biz/bake-cafe-rancho-cucamonga",
      GOOGLE_BUSINESS_PROFILE_URL,
      "https://maps.apple.com/place?place-id=IF8AC277FA2B85D",
      "https://www.doordash.com/store/+bake-cafe-by-take-ur-seat-rancho-cucamonga-33687201/",
    ],
  }
}
